/**
 * services/pwa-install.js — capture beforeinstallprompt + expose install
 * status to the UI (#188).
 *
 * Why this file exists:
 *   - Chrome / Edge (desktop + Android) fire `beforeinstallprompt` ONCE,
 *     very early. If the page doesn't capture it on that single fire, the
 *     PWA install affordance is lost for the session — the only recovery
 *     is a hard reload.
 *   - The captured BeforeInstallPromptEvent has to be triggered later
 *     by a user gesture (button click) — the browser blocks .prompt()
 *     from firing during the original event handler.
 *
 * What we expose:
 *   $canInstall  — boolean atom, true when a deferred prompt is held AND
 *                  the user hasn't dismissed the install CTA.
 *   $isInstalled — boolean atom, true when display-mode is 'standalone'
 *                  (or iOS navigator.standalone). Always true once
 *                  installed.
 *   promptInstall() — fire the cached prompt; resolves with the user's
 *                     choice ('accepted' | 'dismissed' | null when no
 *                     prompt was held). After 'accepted' the cache is
 *                     consumed and $canInstall flips false.
 *   dismissInstallCta() — user said "not now" — persist the dismissal
 *                         so we don't keep showing the button.
 */

import { atom } from 'nanostores';

const _DISMISS_KEY = 'inbox_v2_install_cta_dismissed';

let _deferredPrompt = null;
export const $canInstall  = atom(false);
export const $isInstalled = atom(_detectInstalled());

function _detectInstalled() {
  try {
    if (typeof window === 'undefined') return false;
    if (navigator.standalone === true) return true; // iOS Safari
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) return true;
    return false;
  } catch { return false; }
}

function _isDismissed() {
  try { return localStorage.getItem(_DISMISS_KEY) === '1'; } catch { return false; }
}

/**
 * Wire the global listeners. Idempotent — safe to call multiple times,
 * subsequent calls are no-ops.
 */
let _wired = false;
export function initPwaInstall() {
  if (_wired || typeof window === 'undefined') return;
  _wired = true;

  // Capture the prompt the moment Chrome offers it.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();             // suppress the mini-info bar
    _deferredPrompt = e;
    if (!$isInstalled.get() && !_isDismissed()) $canInstall.set(true);
    console.log('[v2 pwa-install] beforeinstallprompt captured');
  });

  // Once installed, the prompt becomes meaningless.
  window.addEventListener('appinstalled', () => {
    console.log('[v2 pwa-install] appinstalled');
    _deferredPrompt = null;
    $canInstall.set(false);
    $isInstalled.set(true);
  });

  // display-mode can flip while running (rare — usually requires reload),
  // but the listener is cheap insurance.
  if (window.matchMedia) {
    try {
      window.matchMedia('(display-mode: standalone)').addEventListener?.('change', (mq) => {
        if (mq.matches) { $isInstalled.set(true); $canInstall.set(false); }
      });
    } catch {}
  }
}

export async function promptInstall() {
  if (!_deferredPrompt) return null;
  try {
    _deferredPrompt.prompt();
    const choice = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    $canInstall.set(false);
    return choice?.outcome || null;
  } catch (e) {
    console.warn('[v2 pwa-install] prompt failed:', e?.message);
    return null;
  }
}

export function dismissInstallCta() {
  try { localStorage.setItem(_DISMISS_KEY, '1'); } catch {}
  $canInstall.set(false);
}
